/**
 * NavBar - Unified navigation bar component
 * Apple HIG compliant with variants for Brand (dashboard) and Title (subpages)
 *
 * Improvements in this revision:
 * - Tighter spacing for premium feel (px-3 inner, gap-1 actions)
 * - Brand logo uses actual asset (not gradient placeholder)
 * - Badge uses semantic color token + ARIA live for screen readers
 * - Focus-visible rings use consistent 3px offset pattern
 * - Back button chevron slightly larger for easier thumb reach
 * - Glass surface transition smoothed to 200ms for calmer scroll response
 *
 * Layout structure:
 * - Outer wrapper: handles safe-area, z-index, position
 * - Inner nav: glass pill with left/center/right slots
 */

import { memo, useCallback, useMemo, useEffect, type ReactNode, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Inbox } from '@/lib/icons';
import { triggerHaptic } from '@/lib/haptics';
import { usePartnerStore } from '@/state/partner';
import { useAuthStore } from '@/state/auth';
import { useNotificationsStore } from '@/state/notifications';

export type NavBarVariant = 'brand' | 'title';

export interface NavBarProps {
  /** Variant: 'brand' for dashboard, 'title' for subpages */
  variant?: NavBarVariant;
  /** Title text (used for 'title' variant) */
  title?: string;
  /** Show back button (used for 'title' variant) */
  showBack?: boolean;
  /** Custom back handler (defaults to navigate(-1)) */
  onBack?: () => void;
  /** Left slot content (overrides default left content) */
  leftContent?: ReactNode;
  /** Center slot content (overrides title for custom content) */
  centerContent?: ReactNode;
  /** Right slot content */
  rightContent?: ReactNode;
  /** Whether content has scrolled (adds elevation + hairline) */
  hasScrolled?: boolean;
  /** Transparent mode (no background, for hero sections) */
  transparent?: boolean;
}

/**
 * Header height constants (Apple HIG: 44pt interactive row)
 * These are also available as CSS custom properties in tailwind.css
 */
export const HEADER_ROW_HEIGHT = 44; // px - minimum interactive row height
export const HEADER_PILL_HEIGHT = 48; // px - nav pill visual height

/** Glass nav bar styling - Apple HIG 2026 elevation */
const getNavStyle = (hasScrolled: boolean, transparent: boolean): CSSProperties => {
  if (transparent) {
    return {
      height: `${HEADER_PILL_HEIGHT}px`,
      background: 'transparent',
      borderRadius: 'var(--r-nav-pill, 14px)',
    };
  }

  return {
    height: `${HEADER_PILL_HEIGHT}px`,
    background: hasScrolled
      ? 'rgba(255, 255, 255, 0.92)'
      : 'rgba(255, 255, 255, 0.78)',
    backdropFilter: 'blur(24px) saturate(160%)',
    WebkitBackdropFilter: 'blur(24px) saturate(160%)',
    borderRadius: 'var(--r-nav-pill, 14px)',
    border: hasScrolled
      ? '0.5px solid rgba(0, 0, 0, 0.06)'
      : '0.5px solid rgba(0, 0, 0, 0.03)',
    boxShadow: hasScrolled
      ? '0 0.5px 1px rgba(0,0,0,0.02), 0 2px 6px rgba(0,0,0,0.03), 0 6px 16px rgba(0,0,0,0.025)'
      : '0 0.5px 1px rgba(0,0,0,0.01), 0 2px 8px rgba(0,0,0,0.018)',
    transition: 'background 200ms ease-out, box-shadow 200ms ease-out, border-color 200ms ease-out',
  };
};

export const NavBar = memo(function NavBar({
  variant = 'brand',
  title,
  showBack = false,
  onBack,
  leftContent,
  centerContent,
  rightContent,
  hasScrolled = false,
  transparent = false,
}: NavBarProps) {
  const navigate = useNavigate();

  const handleBack = useCallback(() => {
    triggerHaptic();
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  }, [navigate, onBack]);

  const handleBrandClick = useCallback(() => {
    triggerHaptic();
    navigate('/today');
  }, [navigate]);

  // Render left content based on variant
  const renderLeft = () => {
    if (leftContent) return leftContent;

    if (variant === 'brand') {
      return (
        <button
          onClick={handleBrandClick}
          className="flex items-center gap-2 touch-manipulation
                     min-h-[44px] px-1 rounded-xl
                     active:scale-[0.98] motion-safe:transition-transform duration-150
                     focus-visible:outline-none focus-visible:ring-2
                     focus-visible:ring-primary-200/60 focus-visible:ring-offset-1"
          aria-label="Go to Dashboard"
        >
          <img
            src="/logo.svg"
            alt=""
            aria-hidden="true"
            className="flex-shrink-0"
            style={{ width: '28px', height: '28px', borderRadius: '8px' }}
          />
          <span className="text-[17px] font-semibold text-gray-800 tracking-[-0.02em] select-none">
            Postpartner
          </span>
        </button>
      );
    }

    // Title variant with back button
    if (showBack) {
      return (
        <HeaderIconButton onClick={handleBack} label="Go back">
          <ChevronLeft className="w-6 h-6" strokeWidth={2.25} aria-hidden="true" />
        </HeaderIconButton>
      );
    }

    // Empty spacer to maintain layout balance
    return <div className="w-11 flex-shrink-0" aria-hidden="true" />;
  };

  // Render center content
  const renderCenter = () => {
    if (centerContent) return centerContent;

    if (variant === 'title' && title) {
      return (
        <h1 className="text-[17px] font-semibold text-gray-800 tracking-[-0.02em] truncate max-w-[200px] select-none">
          {title}
        </h1>
      );
    }

    return null;
  };

  return (
    <header
      className="flex-shrink-0 z-30"
      role="banner"
      style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 8px)' }}
    >
      {/* Outer padding container */}
      <div className="px-4 pb-2">
        {/* Inner nav pill - glass surface with flex layout */}
        <nav
          className="max-w-lg mx-auto w-full flex items-center justify-between px-2 min-h-[44px]"
          style={getNavStyle(hasScrolled, transparent)}
          role="navigation"
          aria-label="Main navigation"
        >
          {/* Left slot */}
          <div className="flex items-center flex-shrink-0">{renderLeft()}</div>

          {/* Center slot */}
          <div className="flex-1 flex justify-center min-w-0 px-2">
            {renderCenter()}
          </div>

          {/* Right slot */}
          <div className="flex items-center justify-end flex-shrink-0 gap-0.5">
            {rightContent}
          </div>
        </nav>
      </div>
    </header>
  );
});

// ─── HeaderIconButton ────────────────────────────────────────

interface HeaderIconButtonProps {
  onClick: () => void;
  label: string;
  children: ReactNode;
  badge?: number;
  /** ARIA expanded state (for toggles like menu) */
  expanded?: boolean;
  className?: string;
}

/**
 * HeaderIconButton - 44×44 icon button for header actions
 * Apple HIG: 44px touch target, subtle hover/press states, badge support
 */
export const HeaderIconButton = memo(function HeaderIconButton({
  onClick,
  label,
  children,
  badge,
  expanded,
  className = '',
}: HeaderIconButtonProps) {
  const handleClick = useCallback(() => {
    triggerHaptic();
    onClick();
  }, [onClick]);

  const badgeLabel = badge && badge > 0
    ? badge > 9 ? ', 9+ unread' : `, ${badge} unread`
    : '';

  return (
    <button
      onClick={handleClick}
      className={`relative flex items-center justify-center
                 w-11 h-11 min-w-[44px] min-h-[44px]
                 rounded-[12px] text-gray-500
                 hover:bg-black/[0.03] hover:text-gray-600
                 active:bg-black/[0.05] active:scale-[0.96]
                 motion-safe:transition-all duration-150
                 touch-manipulation
                 focus-visible:outline-none focus-visible:ring-2
                 focus-visible:ring-primary-200/60 focus-visible:ring-offset-1
                 ${className}`}
      aria-label={`${label}${badgeLabel}`}
      aria-expanded={expanded}
    >
      {children}
      {/* Notification badge */}
      {badge !== undefined && badge > 0 && (
        <span
          className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full
                     text-white text-[11px] font-semibold flex items-center justify-center
                     pointer-events-none motion-safe:animate-scale-in"
          style={{
            background: 'linear-gradient(to bottom, #EF4444 0%, #DC2626 100%)',
            boxShadow: '0 0.5px 1px rgba(220,38,38,0.2), 0 2px 4px rgba(220,38,38,0.25), inset 0 0.5px 0 rgba(255,255,255,0.2)',
          }}
          aria-hidden="true"
        >
          {badge > 9 ? '9+' : badge}
        </span>
      )}
    </button>
  );
});

// ─── MenuButton ──────────────────────────────────────────────

interface MenuButtonProps {
  onClick: () => void;
  isOpen?: boolean;
}

/**
 * MenuButton - iOS-style two-line hamburger glyph
 * Refined: thinner lines, tighter gap, calmer hover
 */
export const MenuButton = memo(function MenuButton({ onClick, isOpen = false }: MenuButtonProps) {
  return (
    <HeaderIconButton
      onClick={onClick}
      label={isOpen ? 'Close navigation menu' : 'Open navigation menu'}
      expanded={isOpen}
    >
      <div className="flex flex-col justify-center items-end gap-[5px]" aria-hidden="true">
        <div
          className="rounded-full bg-gray-500 motion-safe:transition-colors duration-150"
          style={{ width: '18px', height: '2px' }}
        />
        <div
          className="rounded-full bg-gray-400 motion-safe:transition-colors duration-150"
          style={{ width: '14px', height: '2px' }}
        />
      </div>
    </HeaderIconButton>
  );
});

// ─── InboxButton ─────────────────────────────────────────────

interface InboxButtonProps {
  onClick: () => void;
}

/**
 * InboxButton - Global inbox entry with combined unread badge
 * Sources: partner nudges + notifications
 */
export const InboxButton = memo(function InboxButton({ onClick }: InboxButtonProps) {
  const { recentNudges } = usePartnerStore();
  const { user } = useAuthStore();
  const { unreadCount: notificationUnreadCount, subscribeToNotifications } = useNotificationsStore();

  // Subscribe to notification unread count
  useEffect(() => {
    if (user?.uid) {
      const unsubscribe = subscribeToNotifications(user.uid);
      return unsubscribe;
    }
  }, [user?.uid, subscribeToNotifications]);

  // Count unread nudges (messages sent TO current user that haven't been viewed)
  const messageUnreadCount = useMemo(() => {
    if (!recentNudges || !user?.uid) return 0;
    return recentNudges.filter(
      (nudge) => nudge.toUid === user.uid && nudge.status === 'sent'
    ).length;
  }, [recentNudges, user?.uid]);

  const totalUnreadCount = messageUnreadCount + notificationUnreadCount;

  return (
    <HeaderIconButton
      onClick={onClick}
      label="Inbox"
      badge={totalUnreadCount}
    >
      <Inbox className="w-5 h-5" strokeWidth={1.75} />
    </HeaderIconButton>
  );
});
